import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Mail, Pencil, Plus, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Skeleton } from "@fleet/ui";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { useAdminCapabilities } from "../use-admin-capabilities";
import type { components } from "@jornada/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmailTemplateEditor } from "./email-template-editor";
import { QueryErrorState } from "@/components/ui/query-error-state";

type EmailTemplate = components["schemas"]["EmailTemplate"];

/**
 * CRUD for email templates (campaign #11). Lists the templates (system +
 * custom), allows create/edit/delete. System ones are not deletable.
 */
export function EmailTemplateManager() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_app/admin/email" });

  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  // I2: `.data` ONLY on `isSuccess` — a single memo that both reads pass
  // through (the list and the resolution of the URL's `?template=<id>`).
  const templates = createMemo<EmailTemplate[]>(() =>
    templatesQuery.isSuccess ? (templatesQuery.data ?? []) : [],
  );

  // ── Editor ↔ URL. `?template=new` opens the create editor and `?template=<id>`
  // opens it for that template, so the editor has a copyable link that reopens it
  // (idempotent). open/close write the URL with `replace:true`. The edited
  // template is resolved from the loaded list by id.
  const creating = () => search().template === "new";
  const editing = createMemo<EmailTemplate | null>(() => {
    const id = search().template;
    if (!id || id === "new") {
      return null;
    }
    return templates().find((tpl) => tpl.id === id) ?? null;
  });
  const openCreate = () => {
    void navigate({ to: "/admin/email", search: () => ({ template: "new" }), replace: true });
  };
  const openEdit = (tpl: EmailTemplate) => {
    void navigate({ to: "/admin/email", search: () => ({ template: tpl.id }), replace: true });
  };
  const closeEditor = () => {
    void navigate({ to: "/admin/email", search: () => ({ template: undefined }), replace: true });
  };

  const { can } = useAdminCapabilities();
  const canWrite = () => can("content.write");

  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/email/templates/delete", { body: vars }),
  );

  const handleDelete = async (tpl: EmailTemplate) => {
    if (tpl.isSystem) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id: tpl.id });
      await templatesQuery.refetch();
    } catch (err) {
      console.error("Error deleting email template:", err);
    }
  };

  const onSaved = async () => {
    closeEditor();
    await templatesQuery.refetch();
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Mail class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Email Templates`)}</h2>
        </div>
        {/* Crear NO tiene nada que leer: se corta de raíz. */}
        <Show when={canWrite()}>
          <Button class="gap-2" onClick={() => openCreate()}>
            <Plus class="h-4 w-4" />
            {t(msg`New Template`)}
          </Button>
        </Show>
      </div>

      <Show when={creating() || editing()}>
        <EmailTemplateEditor
          template={editing()}
          onSaved={onSaved}
          onCancel={() => {
            closeEditor();
          }}
        />
      </Show>

      {/* I3 (P23/CC-60): un API caído NO es «no hay nada». El bloque de
          contenido va ENVUELTO, no encadenado: en error `isLoading` es `false`,
          así que un `when={!q.isLoading && !q.isError}` con fallback pintaría
          el esqueleto Y el error a la vez, para siempre. */}
      <Show when={templatesQuery.isError}>
        <QueryErrorState
          testId="admin-email-templates-error"
          onRetry={() => void templatesQuery.refetch()}
        />
      </Show>
      <Show when={!templatesQuery.isError}>
        <Show
          when={!templatesQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <For each={Array.from({ length: 4 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="space-y-3">
            <For each={templates()}>
              {(tpl) => (
                <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="truncate font-semibold">{tpl.name}</span>
                      <Badge variant="secondary">{tpl.category}</Badge>
                      <Show when={tpl.isSystem}>
                        <Badge>{t(msg`System`)}</Badge>
                      </Show>
                      <Show when={!tpl.isActive}>
                        <Badge variant="outline">{t(msg`Inactive`)}</Badge>
                      </Show>
                    </div>
                    <p class="mt-1 truncate text-sm text-muted-foreground">{tpl.subject}</p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                      {(() => {
                        const { key } = tpl;
                        const version = tpl.currentVersion;
                        return t(msg`Key: ${key} · v${version}`);
                      })()}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-2">
                    {/* El editor se abre en MODO LECTURA sin content.write: el
                      cuerpo de la plantilla ya viaja en el GET de la lista, así
                      que `read_only` no pierde información. */}
                    <Button variant="ghost" size="sm" class="gap-1" onClick={() => openEdit(tpl)}>
                      <Pencil class="h-4 w-4" />
                      {canWrite() ? t(msg`Edit`) : t(msg`View`)}
                    </Button>
                    <Show when={!tpl.isSystem && canWrite()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1 text-destructive"
                        disabled={deleteMutation.isPending}
                        onClick={() => handleDelete(tpl)}
                      >
                        <Trash2 class="h-4 w-4" />
                        {t(msg`Delete`)}
                      </Button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
